/**
 * WHATSAPP MESSAGE API — Wrapper REST Liferay allineato a
 * WhatsAppMessagesResourceImpl.java.
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET  /whatsapp-messages/{groupId}         pagination -> Page<WhatsAppMessage>
 *   - GET  /whatsapp-message/{whatsAppMessageId}           -> WhatsAppMessage
 *   - POST /whatsapp-messages/{groupId}/send  WhatsAppMessageInput -> WhatsAppMessage
 *
 * I messaggi inbound nascono dal webhook Meta; l'unica scrittura dal CRM è
 * l'invio (`create` = POST .../send). Non esistono update/delete lato backend.
 * `getAll`/`getById` servono solo al registry entità (ricerca globale + label),
 * non c'è una lista di messaggi lato UI: la lettura è per sessione.
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import {
  whatsAppMessageFromDto,
  whatsAppMessageSendToInput,
  whatsAppNumberFromDto,
  whatsAppTemplateFromDto,
  whatsAppTemplateSendToInput,
  type WhatsAppMessageDto,
  type WhatsAppNumberDto,
  type WhatsAppTemplateDto,
} from './whatsAppMessageMappers';
import type {
  WhatsAppMediaSendPayload,
  WhatsAppMessage,
  WhatsAppMessageSendPayload,
  WhatsAppNumber,
  WhatsAppTemplate,
  WhatsAppTemplateSendPayload,
} from './whatsAppMessageTypes';

const COLLECTION = '/whatsapp-messages';
const SINGLE = '/whatsapp-message';
const TEMPLATES = '/whatsapp-templates';
const NUMBERS = '/whatsapp-numbers';

export const whatsAppMessageApi = {
  getAll: (
    groupId: number,
    params?: ServerListParams,
  ): Promise<Page<WhatsAppMessage>> =>
    httpClient
      .get<Page<WhatsAppMessageDto>>(`${COLLECTION}/${groupId}`, {
        params: params as Record<string, unknown> | undefined,
      })
      .then((r) => ({
        ...r.data,
        items: r.data.items.map(whatsAppMessageFromDto),
      })),

  getById: (whatsAppMessageId: number): Promise<WhatsAppMessage> =>
    httpClient
      .get<WhatsAppMessageDto>(`${SINGLE}/${whatsAppMessageId}`)
      .then((r) => whatsAppMessageFromDto(r.data)),

  // `create` = invio outbound: POST /whatsapp-messages/{groupId}/send.
  create: (
    groupId: number,
    payload: WhatsAppMessageSendPayload,
  ): Promise<WhatsAppMessage> =>
    httpClient
      .post<WhatsAppMessageDto>(
        `${COLLECTION}/${groupId}/send`,
        whatsAppMessageSendToInput(payload),
      )
      .then((r) => whatsAppMessageFromDto(r.data)),

  // Contenuto binario del media (immagine/audio/video/documento) come Blob, per
  // rendering inline o download: GET /whatsapp-message/{id}/media/content.
  getMediaContent: (whatsAppMessageId: number): Promise<Blob> =>
    httpClient
      .getBlob(`${SINGLE}/${whatsAppMessageId}/media/content`)
      .then((r) => r.data),

  // Invio media: POST multipart /whatsapp-messages/{groupId}/send-media (file +
  // personId + phoneNumber + caption). tipo/mimeType derivati dal file lato server.
  sendMedia: (
    groupId: number,
    payload: WhatsAppMediaSendPayload,
  ): Promise<WhatsAppMessage> => {
    const form = new FormData();
    form.append('personId', String(payload.personId));
    form.append('phoneNumber', payload.phoneNumber);
    if (payload.caption) form.append('caption', payload.caption);
    if (payload.phoneNumberId)
      form.append('phoneNumberId', payload.phoneNumberId);
    form.append('file', payload.file, payload.file.name);
    return httpClient
      .post<WhatsAppMessageDto>(`${COLLECTION}/${groupId}/send-media`, form)
      .then((r) => whatsAppMessageFromDto(r.data));
  },

  // Numeri WhatsApp business mittente configurati per la company: popolano la
  // combo di scelta del mittente. GET /whatsapp-numbers/{groupId}.
  getNumbers: (groupId: number): Promise<WhatsAppNumber[]> =>
    httpClient
      .get<Page<WhatsAppNumberDto>>(`${NUMBERS}/${groupId}`)
      .then((r) => r.data.items.map(whatsAppNumberFromDto)),

  // Template approvati da Meta, letti live dal WhatsApp Business Account: sono
  // gli unici messaggi consentiti a finestra di 24h chiusa.
  getTemplates: (groupId: number): Promise<WhatsAppTemplate[]> =>
    httpClient
      .get<Page<WhatsAppTemplateDto>>(`${TEMPLATES}/${groupId}`)
      .then((r) => r.data.items.map(whatsAppTemplateFromDto)),

  sendTemplate: (
    groupId: number,
    payload: WhatsAppTemplateSendPayload,
  ): Promise<WhatsAppMessage> =>
    httpClient
      .post<WhatsAppMessageDto>(
        `${COLLECTION}/${groupId}/send-template`,
        whatsAppTemplateSendToInput(payload),
      )
      .then((r) => whatsAppMessageFromDto(r.data)),

  // Trascrizione della nota vocale: POST /whatsapp-message/{id}/transcribe.
  // Restituisce il messaggio aggiornato (testo + riassunto + action items).
  //
  // Sincrona e lenta per natura (l'audio va a un motore esterno), ma idempotente
  // lato server: un messaggio già trascritto torna subito, senza richiamare il
  // motore. Ripremere il tasto non costa nulla.
  transcribe: (whatsAppMessageId: number): Promise<WhatsAppMessage> =>
    httpClient
      .post<WhatsAppMessageDto>(`${SINGLE}/${whatsAppMessageId}/transcribe`)
      .then((r) => whatsAppMessageFromDto(r.data)),
};
